import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, TaskStatus, TaskPriority, Collection } from '../types';
import { getProjectTasks, getProjectCollections } from '../services/taskService';
import { dvUpdate } from '../api/dataverseClient';
import TaskDetailDrawer from '../task/TaskDetailDrawer';
import PeoplePicker, { Person } from '../components/PeoplePicker';
import { TagPills, TagMultiFilter } from '../components/TagPills';
import { Tag, TagLink, TASK_TAG_POOL, getProjectTagLinks } from '../services/tagService';
import { useTaskPolling, mergeTaskDeltas } from '../hooks/useTaskPolling';

const STATUS_LABEL: Record<number, string> = {
  [TaskStatus.NotStarted]: 'Not Started',
  [TaskStatus.InProgress]: 'In Progress',
  [TaskStatus.InReview]:   'In Review',
  [TaskStatus.Done]:       'Done',
  [TaskStatus.OnHold]:     'On Hold',
  [TaskStatus.Cancelled]:  'Cancelled',
};
const STATUS_VALUES = [
  TaskStatus.NotStarted, TaskStatus.InProgress, TaskStatus.InReview,
  TaskStatus.Done, TaskStatus.OnHold, TaskStatus.Cancelled,
];

const PRIORITY_LABEL: Record<number, string> = {
  [TaskPriority.Critical]: 'Critical',
  [TaskPriority.High]:     'High',
  [TaskPriority.Medium]:   'Medium',
  [TaskPriority.Low]:      'Low',
};
const PRIORITY_VALUES = [
  TaskPriority.Critical, TaskPriority.High, TaskPriority.Medium, TaskPriority.Low,
];
// For sorting Priority column asc: None→Low→Medium→High→Critical — map to numeric rank.
const PRIORITY_RANK: Record<number, number> = {
  [TaskPriority.Low]:      1,
  [TaskPriority.Medium]:   2,
  [TaskPriority.High]:     3,
  [TaskPriority.Critical]: 4,
};

type SortKey = 'title' | 'due' | 'priority' | 'status' | 'pct' | null;
type SortDir = 'asc' | 'desc';

interface Props {
  pca: IPublicClientApplication;
  projectId: string;
  refreshKey?: number;
}

export default function ListView({ pca, projectId, refreshKey }: Props) {
  const [tasks, setTasks]           = useState<Task[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [selected, setSelected]     = useState<Task | null>(null);

  const [sortKey, setSortKey]       = useState<SortKey>(null);  // null = default (createdon desc)
  const [sortDir, setSortDir]       = useState<SortDir>('asc');

  const [search, setSearch]                 = useState('');
  const [statusFilter, setStatusFilter]     = useState<number | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<number | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  const [collectionFilter, setCollectionFilter] = useState<string>('');  // '' = any collection
  const [tagFilter, setTagFilter]           = useState<string[]>([]);
  const [tagLinks, setTagLinks]             = useState<Map<string, TagLink[]>>(new Map());

  const [editing, setEditing] = useState<{ id: string; col: 'title' | 'status' | 'priority' | 'due' } | null>(null);
  const [editDraft, setEditDraft] = useState<string>('');

  // Bulk multi-select — actions scoped to status / priority / assignee.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState<Person | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setTasks(await getProjectTasks(pca, projectId));
      // Collections drive the Collection column label + filter. Best-effort —
      // a failure just leaves the column showing "—" and an empty filter list.
      getProjectCollections(pca, projectId)
        .then(setCollections)
        .catch(() => setCollections([]));
      // Tags load is best-effort — a failure must not take down the list.
      getProjectTagLinks(pca, TASK_TAG_POOL, projectId)
        .then(setTagLinks)
        .catch(() => setTagLinks(new Map()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [pca, projectId]);

  useEffect(() => { loadData(); }, [loadData, refreshKey]);

  // Real-time refresh — deferred while the drawer, an inline cell edit, or
  // a bulk selection is open so an incoming poll can never overwrite
  // in-progress input or reshuffle rows mid-selection.
  useTaskPolling({
    pca, projectId,
    isPaused: () => loading || selected !== null || editing !== null || bulkBusy || checkedIds.size > 0,
    onTasks: deltas => setTasks(prev => mergeTaskDeltas(prev, deltas)),
  });

  // Collection id → name, for the Collection column + filter labels.
  const collById = useMemo(() => {
    const m = new Map<string, string>();
    collections.forEach(c => m.set(c.dagrun_pm_collectionid, c.dagrun_pm_name));
    return m;
  }, [collections]);

  // Unique assignees for the filter dropdown
  const assignees = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach(t => { if (t.dagrun_pm_assigneename) set.add(t.dagrun_pm_assigneename); });
    return Array.from(set).sort();
  }, [tasks]);

  // Unique tags in use across this project's tasks — the filter options.
  const tagOptions = useMemo(() => {
    const byId = new Map<string, Tag>();
    tagLinks.forEach(links => links.forEach(l => byId.set(l.tag.id, l.tag)));
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [tagLinks]);

  const tagsForTask = useCallback(
    (taskId: string): Tag[] => (tagLinks.get(taskId) ?? []).map(l => l.tag),
    [tagLinks]
  );

  // Apply filters → then sort
  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = tasks.filter(t => {
      if (q && !t.dagrun_pm_name.toLowerCase().includes(q)) return false;
      if (statusFilter !== '' && t.dagrun_pm_status !== statusFilter) return false;
      if (priorityFilter !== '' && t.dagrun_pm_priority !== priorityFilter) return false;
      if (assigneeFilter && t.dagrun_pm_assigneename !== assigneeFilter) return false;
      if (collectionFilter) {
        if (collectionFilter === '__none') {
          if (t._dagrun_pm_wipcollectionid_value) return false;
        } else if (t._dagrun_pm_wipcollectionid_value !== collectionFilter) return false;
      }
      if (tagFilter.length) {
        // OR logic: the task must carry at least one of the selected tags.
        const ids = (tagLinks.get(t.dagrun_pm_taskid) ?? []).map(l => l.tag.id);
        if (!tagFilter.some(id => ids.includes(id))) return false;
      }
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return a.dagrun_pm_name.localeCompare(b.dagrun_pm_name) * dir;
        case 'due': {
          const av = a.dagrun_pm_duedate ? new Date(a.dagrun_pm_duedate).getTime() : Infinity;
          const bv = b.dagrun_pm_duedate ? new Date(b.dagrun_pm_duedate).getTime() : Infinity;
          return (av - bv) * dir;
        }
        case 'priority': {
          const av = PRIORITY_RANK[a.dagrun_pm_priority] ?? 0;
          const bv = PRIORITY_RANK[b.dagrun_pm_priority] ?? 0;
          return (av - bv) * dir;
        }
        case 'status':
          return (a.dagrun_pm_status - b.dagrun_pm_status) * dir;
        case 'pct':
          return ((a.dagrun_pm_percentcomplete ?? 0) - (b.dagrun_pm_percentcomplete ?? 0)) * dir;
        case null:
        default: {
          // Default: createdon desc (newest first)
          const av = a.createdon ? new Date(a.createdon).getTime() : 0;
          const bv = b.createdon ? new Date(b.createdon).getTime() : 0;
          return bv - av;
        }
      }
    });
    return rows;
  }, [tasks, search, statusFilter, priorityFilter, assigneeFilter, collectionFilter, tagFilter, tagLinks, sortKey, sortDir]);

  const toggleSort = (k: Exclude<SortKey, null>) => {
    if (sortKey !== k) { setSortKey(k); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc'); // third click clears sort
  };

  const sortIndicator = (k: Exclude<SortKey, null>) =>
    sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const clearFilters = () => {
    setSearch(''); setStatusFilter(''); setPriorityFilter(''); setAssigneeFilter('');
    setCollectionFilter(''); setTagFilter([]);
  };

  const patch = async (taskId: string, fields: Partial<Task>) => {
    setTasks(prev => prev.map(t => t.dagrun_pm_taskid === taskId ? { ...t, ...fields } : t));
    try {
      await dvUpdate(pca, 'dagrun_pm_tasks', taskId, fields as Record<string, unknown>);
    } catch {
      loadData(); // revert on failure
    }
  };

  const startEdit = (task: Task, col: 'title' | 'status' | 'priority' | 'due') => {
    setEditing({ id: task.dagrun_pm_taskid, col });
    if (col === 'title')    setEditDraft(task.dagrun_pm_name);
    if (col === 'due')      setEditDraft(task.dagrun_pm_duedate?.slice(0, 10) ?? '');
    if (col === 'status')   setEditDraft(String(task.dagrun_pm_status));
    if (col === 'priority') setEditDraft(String(task.dagrun_pm_priority));
  };
  const cancelEdit = () => { setEditing(null); setEditDraft(''); };

  const commitTitle = (task: Task) => {
    const val = editDraft.trim();
    if (val && val !== task.dagrun_pm_name) patch(task.dagrun_pm_taskid, { dagrun_pm_name: val });
    cancelEdit();
  };
  const commitDue = (task: Task) => {
    const val = editDraft || null;
    if (val !== (task.dagrun_pm_duedate?.slice(0, 10) ?? null)) {
      patch(task.dagrun_pm_taskid, { dagrun_pm_duedate: val });
    }
    cancelEdit();
  };
  const commitStatus = (task: Task, val: number) => {
    if (val !== task.dagrun_pm_status) patch(task.dagrun_pm_taskid, { dagrun_pm_status: val });
    cancelEdit();
  };
  const commitPriority = (task: Task, val: number) => {
    if (val !== task.dagrun_pm_priority) patch(task.dagrun_pm_taskid, { dagrun_pm_priority: val });
    cancelEdit();
  };

  // ── Bulk actions ──────────────────────────────────────────────────
  const toggleChecked = (taskId: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };

  const bulkApply = async (fields: Record<string, unknown>) => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    setTasks(prev => prev.map(t => checkedIds.has(t.dagrun_pm_taskid) ? { ...t, ...fields } : t));
    try {
      await Promise.all(ids.map(id => dvUpdate(pca, 'dagrun_pm_tasks', id, fields)));
    } catch {
      loadData(); // reconcile on any failure
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--ink-3)' }}>Loading tasks…</div>;
  if (error)   return <div style={{ padding: 24, color: 'var(--danger)' }}>Error: {error}</div>;

  const activeFilters =
    search.trim() !== '' ||
    statusFilter !== '' ||
    priorityFilter !== '' ||
    assigneeFilter !== '' ||
    collectionFilter !== '' ||
    tagFilter.length > 0;

  return (
    <div style={{ padding: 14 }}>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="🔍  Filter tasks…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 220px', minWidth: 180, fontSize: 13 }}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value === '' ? '' : Number(e.target.value))}
          style={{ width: 140 }}
        >
          <option value="">Any status</option>
          {STATUS_VALUES.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
        </select>
        <select
          value={priorityFilter}
          onChange={e => setPriorityFilter(e.target.value === '' ? '' : Number(e.target.value))}
          style={{ width: 130 }}
        >
          <option value="">Any priority</option>
          {PRIORITY_VALUES.map(v => <option key={v} value={v}>{PRIORITY_LABEL[v]}</option>)}
        </select>
        <select
          value={assigneeFilter}
          onChange={e => setAssigneeFilter(e.target.value)}
          style={{ width: 150 }}
        >
          <option value="">Any assignee</option>
          {assignees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={collectionFilter}
          onChange={e => setCollectionFilter(e.target.value)}
          style={{ width: 160 }}
          title="Filter by collection"
        >
          <option value="">Any collection</option>
          {collections.map(c => (
            <option key={c.dagrun_pm_collectionid} value={c.dagrun_pm_collectionid}>{c.dagrun_pm_name}</option>
          ))}
          <option value="__none">No collection</option>
        </select>
        <TagMultiFilter options={tagOptions} selected={tagFilter} onChange={setTagFilter} />
        {activeFilters && (
          <button className="btn-ghost" onClick={clearFilters} style={{ fontSize: 12 }}>× Clear</button>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 10, fontFamily: 'var(--font-mono)' }}>
        Showing {filteredSorted.length} of {tasks.length} task{tasks.length === 1 ? '' : 's'}
      </div>

      {/* Bulk action bar — appears with 1+ selected. Actions are scoped to
          status / priority / assignee only; each applies immediately to
          every selected task. */}
      {checkedIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '8px 12px', marginBottom: 10,
          background: 'var(--brand-mist)', border: '1px solid var(--brand)',
          borderRadius: 'var(--r-md)',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand)', fontFamily: 'var(--font-ui)' }}>
            {checkedIds.size} selected
          </span>
          <select
            value=""
            disabled={bulkBusy}
            onChange={e => { if (e.target.value !== '') bulkApply({ dagrun_pm_status: Number(e.target.value) }); }}
            style={{ width: 150, fontSize: 12 }}
          >
            <option value="">Change status…</option>
            {STATUS_VALUES.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
          </select>
          <select
            value=""
            disabled={bulkBusy}
            onChange={e => { if (e.target.value !== '') bulkApply({ dagrun_pm_priority: Number(e.target.value) }); }}
            style={{ width: 150, fontSize: 12 }}
          >
            <option value="">Change priority…</option>
            {PRIORITY_VALUES.map(v => <option key={v} value={v}>{PRIORITY_LABEL[v]}</option>)}
          </select>
          <div style={{ width: 220 }}>
            <PeoplePicker
              pca={pca}
              value={bulkAssignee}
              onChange={p => {
                setBulkAssignee(p);
                if (p) {
                  bulkApply({ dagrun_pm_assigneeid: p.aadObjectId, dagrun_pm_assigneename: p.displayName });
                  setBulkAssignee(null);
                }
              }}
              placeholder="Change assignee…"
              disabled={bulkBusy}
            />
          </div>
          {bulkBusy && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Applying…</span>}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => setCheckedIds(new Set())} disabled={bulkBusy}
            style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}
          >Clear selection</button>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'var(--font-ui)' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', color: 'var(--ink-3)' }}>
            <th style={{ width: 32, padding: '8px 6px 8px 11px' }}>
              <input
                type="checkbox"
                aria-label="Select all visible tasks"
                checked={filteredSorted.length > 0 && filteredSorted.every(t => checkedIds.has(t.dagrun_pm_taskid))}
                onChange={e => {
                  if (e.target.checked) setCheckedIds(new Set(filteredSorted.map(t => t.dagrun_pm_taskid)));
                  else setCheckedIds(new Set());
                }}
                style={{ width: 14, height: 14, cursor: 'pointer' }}
              />
            </th>
            <ThSort label="Title"    active={sortKey === 'title'}    dir={sortDir} indicator={sortIndicator('title')}    onClick={() => toggleSort('title')} />
            <ThSort label="Status"   active={sortKey === 'status'}   dir={sortDir} indicator={sortIndicator('status')}   onClick={() => toggleSort('status')} />
            <ThSort label="Priority" active={sortKey === 'priority'} dir={sortDir} indicator={sortIndicator('priority')} onClick={() => toggleSort('priority')} />
            <Th label="Collection" />
            <Th label="Assignee" />
            <Th label="Tags" />
            <ThSort label="Due Date" active={sortKey === 'due'}      dir={sortDir} indicator={sortIndicator('due')}      onClick={() => toggleSort('due')} />
            <ThSort label="% Done"   active={sortKey === 'pct'}      dir={sortDir} indicator={sortIndicator('pct')}      onClick={() => toggleSort('pct')} />
            <th style={{ width: 36 }} />
          </tr>
        </thead>
        <tbody>
          {filteredSorted.map(task => {
            const isEdit = (col: 'title' | 'status' | 'priority' | 'due') =>
              editing && editing.id === task.dagrun_pm_taskid && editing.col === col;

            return (
              <tr
                key={task.dagrun_pm_taskid}
                className="listview-row"
                style={{
                  borderBottom: '0.5px solid var(--border-light)',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                {/* Select */}
                <td style={{ padding: '8px 6px 8px 11px' }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${task.dagrun_pm_name}`}
                    checked={checkedIds.has(task.dagrun_pm_taskid)}
                    onChange={() => toggleChecked(task.dagrun_pm_taskid)}
                    style={{ width: 14, height: 14, cursor: 'pointer' }}
                  />
                </td>
                {/* Title */}
                <td
                  style={{ padding: '8px 11px', color: 'var(--ink)', cursor: 'text', minWidth: 180 }}
                  onClick={() => !isEdit('title') && startEdit(task, 'title')}
                >
                  {isEdit('title') ? (
                    <input
                      autoFocus
                      value={editDraft}
                      onChange={e => setEditDraft(e.target.value)}
                      onBlur={() => commitTitle(task)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      style={{ fontSize: 13, padding: '4px 6px' }}
                    />
                  ) : (
                    task.dagrun_pm_name
                  )}
                </td>

                {/* Status */}
                <td
                  style={{ padding: '8px 11px', color: 'var(--ink-3)', cursor: 'pointer' }}
                  onClick={() => !isEdit('status') && startEdit(task, 'status')}
                >
                  {isEdit('status') ? (
                    <select
                      autoFocus
                      value={editDraft}
                      onChange={e => { setEditDraft(e.target.value); commitStatus(task, Number(e.target.value)); }}
                      onBlur={cancelEdit}
                      style={{ fontSize: 13, padding: '4px 6px' }}
                    >
                      {STATUS_VALUES.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
                    </select>
                  ) : (
                    STATUS_LABEL[task.dagrun_pm_status] ?? '—'
                  )}
                </td>

                {/* Priority */}
                <td
                  style={{ padding: '8px 11px', color: 'var(--ink-3)', cursor: 'pointer' }}
                  onClick={() => !isEdit('priority') && startEdit(task, 'priority')}
                >
                  {isEdit('priority') ? (
                    <select
                      autoFocus
                      value={editDraft}
                      onChange={e => { setEditDraft(e.target.value); commitPriority(task, Number(e.target.value)); }}
                      onBlur={cancelEdit}
                      style={{ fontSize: 13, padding: '4px 6px' }}
                    >
                      {PRIORITY_VALUES.map(v => <option key={v} value={v}>{PRIORITY_LABEL[v]}</option>)}
                    </select>
                  ) : (
                    PRIORITY_LABEL[task.dagrun_pm_priority] ?? '—'
                  )}
                </td>

                {/* Collection — read-only badge (edit via drawer / board) */}
                <td style={{ padding: '8px 11px' }}>
                  {task._dagrun_pm_wipcollectionid_value && collById.has(task._dagrun_pm_wipcollectionid_value) ? (
                    <span style={{
                      display: 'inline-block', maxWidth: 150,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      verticalAlign: 'bottom',
                      fontSize: 11, fontWeight: 500, fontFamily: 'var(--font-ui)',
                      color: 'var(--ink-2)', background: 'var(--surface-2)',
                      border: '0.5px solid var(--border-light)',
                      borderRadius: 999, padding: '2px 9px',
                    }}>{collById.get(task._dagrun_pm_wipcollectionid_value)}</span>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--ink-5)' }}>—</span>
                  )}
                </td>

                {/* Assignee — inline PeoplePicker popover */}
                <td style={{ padding: '8px 11px', color: 'var(--ink-3)' }} onClick={e => e.stopPropagation()}>
                  <InlineAssigneePicker
                    pca={pca}
                    currentId={task.dagrun_pm_assigneeid ?? null}
                    currentName={task.dagrun_pm_assigneename ?? null}
                    onChange={async (p) => {
                      const fields = {
                        dagrun_pm_assigneeid: p?.aadObjectId ?? null,
                        dagrun_pm_assigneename: p?.displayName ?? null,
                      };
                      try {
                        await dvUpdate(pca, 'dagrun_pm_tasks', task.dagrun_pm_taskid, fields);
                        setTasks(prev => prev.map(x => x.dagrun_pm_taskid === task.dagrun_pm_taskid ? { ...x, ...fields } : x));
                      } catch { /* best-effort */ }
                    }}
                  />
                </td>

                {/* Tags — display-only pills, truncated at 3 */}
                <td style={{ padding: '8px 11px' }}>
                  <TagPills tags={tagsForTask(task.dagrun_pm_taskid)} />
                </td>

                {/* Due */}
                <td
                  style={{ padding: '8px 11px', color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                  onClick={() => !isEdit('due') && startEdit(task, 'due')}
                >
                  {isEdit('due') ? (
                    <input
                      autoFocus
                      type="date"
                      value={editDraft}
                      onChange={e => setEditDraft(e.target.value)}
                      onBlur={() => commitDue(task)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      style={{ fontSize: 13, padding: '4px 6px' }}
                    />
                  ) : (
                    task.dagrun_pm_duedate ? new Date(task.dagrun_pm_duedate).toLocaleDateString() : '—'
                  )}
                </td>

                {/* % Done */}
                <td style={{ padding: '8px 11px' }}>
                  <PercentCell pct={task.dagrun_pm_percentcomplete ?? 0} />
                </td>

                {/* Open-drawer icon */}
                <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                  <button
                    type="button"
                    title="Open task"
                    className="listview-open"
                    onClick={() => setSelected(task)}
                    style={{
                      background: 'transparent', border: 'none',
                      color: 'var(--ink-4)', cursor: 'pointer',
                      fontSize: 14, padding: 4, lineHeight: 1,
                      opacity: 0.5, transition: 'opacity 0.12s, color 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--brand)'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = 'var(--ink-4)'; }}
                  >↗</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <TaskDetailDrawer
        task={selected}
        pca={pca}
        onClose={() => setSelected(null)}
        onSaved={loadData}
        onDeleted={(taskId) => setTasks(prev => prev.filter(t => t.dagrun_pm_taskid !== taskId))}
      />
    </div>
  );
}

function Th({ label }: { label: string }) {
  return (
    <th style={{ padding: '8px 11px', fontWeight: 500, fontSize: 12 }}>{label}</th>
  );
}

function ThSort({
  label, active, dir, indicator, onClick,
}: { label: string; active: boolean; dir: SortDir; indicator: string; onClick: () => void }) {
  return (
    <th
      onClick={onClick}
      style={{
        padding: '8px 11px', fontWeight: 500, fontSize: 12,
        cursor: 'pointer', userSelect: 'none',
        color: active ? 'var(--brand)' : 'var(--ink-3)',
      }}
      title={active ? `Sort ${dir}ending — click again to ${dir === 'asc' ? 'descend' : 'clear'}` : 'Click to sort'}
    >
      {label}{indicator}
    </th>
  );
}

// Inline assignee picker for ListView rows. Button shows current name; click
// opens PeoplePicker in a popover anchored beneath the cell. Popover flips to
// open upward when there isn't enough space below the viewport.
function InlineAssigneePicker({ pca, currentId, currentName, onChange }: {
  pca: IPublicClientApplication;
  currentId: string | null;
  currentName: string | null;
  onChange: (p: Person | null) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // Flip the popover to open upward if the bottom of its intended position
    // would extend past the viewport.
    const el = wrap.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setFlipUp(spaceBelow < 220);
    }
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={wrap} style={{ position: 'relative', minWidth: 120 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          color: currentName ? 'var(--ink-3)' : 'var(--ink-5)',
          fontSize: 13, fontFamily: 'var(--font-ui)',
          fontStyle: currentName ? 'normal' : 'italic',
          textAlign: 'left',
        }}
      >{currentName ?? 'Unassigned'}</button>
      {open && (
        <div style={{
          position: 'absolute',
          left: 0,
          ...(flipUp ? { bottom: '100%', marginBottom: 4 } : { top: '100%', marginTop: 4 }),
          background: '#fff',
          border: '1px solid #DDE0E6', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(15,17,23,0.12)',
          padding: 8, zIndex: 50, minWidth: 260,
          maxWidth: 'calc(100vw - 32px)',
        }}>
          <PeoplePicker
            pca={pca}
            value={currentId && currentName ? { aadObjectId: currentId, displayName: currentName } : null}
            onChange={async (p) => {
              await onChange(p);
              setOpen(false);
            }}
            placeholder="Assign to…"
          />
        </div>
      )}
    </div>
  );
}

function PercentCell({ pct }: { pct: number }) {
  const v = Math.max(0, Math.min(100, pct));
  const done = v >= 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        width: 48, height: 4, background: 'var(--surface-2)',
        borderRadius: 2, overflow: 'hidden',
      }}>
        <div style={{
          width: `${v}%`, height: '100%',
          background: done ? '#16A34A' : 'var(--brand)',
          transition: 'width 0.2s',
        }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', minWidth: 30 }}>
        {v}%
      </span>
    </div>
  );
}
